import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableFooter from '@mui/material/TableFooter';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../../lib/price';
import { parseISO } from 'date-fns';
import { useDateTimeFormat } from '../../preferences/DateTimeFormatContext';
import { goalProgress } from './goalProgress';
import { goalSchedule, inLargestUnit } from './goalSchedule';
import { GoalTaskTypeIcon } from './GoalTaskTypeIcon';
import type { Goal, GoalTask } from '../../api/types';

interface Props {
  goal: Goal;
  canEdit: boolean;
  onToggleTask: (task: GoalTask, completed: boolean) => void;
  onEditTask: (task: GoalTask) => void;
  onDeleteTask: (task: GoalTask) => void;
  /** Given when tasks may be added — renders an empty "add one more" row last. */
  onAddTask?: () => void;
}

/**
 * A goal's tasks as a table — tick box, buy/do, task, price, actions — with
 * the costs added up in the footer like the bottom of an estimate, every
 * amount lined up in the price column, and — once jobs carry durations — how
 * long the rest should take and when the goal may be done.
 */
export function GoalTaskTable({
  goal,
  canEdit,
  onToggleTask,
  onEditTask,
  onDeleteTask,
  onAddTask,
}: Props) {
  const { t } = useTranslation();
  const { formatDate } = useDateTimeFormat();
  const { done, total, remainingCents, totalCents, unpriced, hasPrices } = goalProgress(goal);
  const { remainingDays, estimatedFinish, unestimatedJobs, daysLate } = goalSchedule(goal);
  const timeLeft = inLargestUnit(remainingDays);
  // tick box + type + task — the columns a footer label spans.
  const labelSpan = 3;
  const fullSpan = labelSpan + 1 + (canEdit ? 1 : 0);

  return (
    <Table size="small" aria-label={t('garageCar.goals.tasks.tableLabel', { goal: goal.title })}>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" />
          <TableCell padding="none" />
          <TableCell>{t('garageCar.goals.tasks.columns.task')}</TableCell>
          <TableCell align="right">{t('garageCar.goals.tasks.columns.price')}</TableCell>
          {canEdit && <TableCell padding="none" />}
        </TableRow>
      </TableHead>

      <TableBody>
        {goal.tasks.map((task) => {
          const completed = task.status === 'completed';
          return (
            <TableRow key={task.id}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={completed}
                  disabled={!canEdit}
                  onChange={(e) => onToggleTask(task, e.target.checked)}
                  inputProps={{
                    'aria-label': t('garageCar.goals.tasks.complete', {
                      title: task.title,
                    }),
                  }}
                />
              </TableCell>
              <TableCell padding="none" sx={{ width: 32 }}>
                <GoalTaskTypeIcon type={task.type} />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color={completed ? 'text.secondary' : 'text.primary'}
                  sx={completed ? { textDecoration: 'line-through' } : undefined}
                >
                  {task.title}
                </Typography>
                {task.note && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {task.note}
                  </Typography>
                )}
                {task.duration_value != null && task.duration_unit && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('garageCar.goals.tasks.takes', {
                      duration: t(`garageCar.goals.duration.${task.duration_unit}`, {
                        count: task.duration_value,
                      }),
                    })}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {task.price_cents != null ? formatPrice(task.price_cents) : '—'}
              </TableCell>
              {canEdit && (
                <TableCell padding="none" sx={{ whiteSpace: 'nowrap', width: 72 }}>
                  <Stack direction="row">
                    <IconButton
                      size="small"
                      onClick={() => onEditTask(task)}
                      aria-label={t('garageCar.goals.tasks.edit')}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onDeleteTask(task)}
                      aria-label={t('garageCar.goals.tasks.delete')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          );
        })}
        {onAddTask && (
          // An empty next line: press it to add one more, like the last row of a list.
          <TableRow
            hover
            role="button"
            tabIndex={0}
            aria-label={t('garageCar.goals.tasks.addRow')}
            onClick={onAddTask}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onAddTask();
              }
            }}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell padding="checkbox" />
            <TableCell padding="none">
              <AddIcon fontSize="small" color="action" />
            </TableCell>
            <TableCell colSpan={canEdit ? 3 : 2}>
              <Typography variant="body2" color="text.secondary">
                {t('garageCar.goals.tasks.addRow')}
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>

      {(hasPrices || estimatedFinish) && (
        <TableFooter>
          {hasPrices && (
            <>
              <TotalsRow
                span={labelSpan}
                trailing={canEdit}
                label={t('garageCar.goals.totals.spent', { done, total })}
                amount={formatPrice(totalCents - remainingCents)}
              />
              <TotalsRow
                span={labelSpan}
                trailing={canEdit}
                label={t('garageCar.goals.totals.toFinish', {
                  count: total - done,
                })}
                amount={formatPrice(remainingCents)}
              />
              <TotalsRow
                span={labelSpan}
                trailing={canEdit}
                label={t('garageCar.goals.totals.total')}
                amount={formatPrice(totalCents)}
                strong
              />
              {unpriced > 0 && (
                <NoteRow span={fullSpan}>
                  {t('garageCar.goals.totals.unpriced', { count: unpriced })}
                </NoteRow>
              )}
            </>
          )}

          {estimatedFinish && (
            <>
              <TotalsRow
                span={labelSpan}
                trailing={canEdit}
                label={t('garageCar.goals.schedule.timeToFinish')}
                amount={t(`garageCar.goals.duration.${timeLeft.unit}`, {
                  count: timeLeft.value,
                })}
              />
              <TotalsRow
                span={labelSpan}
                trailing={canEdit}
                label={t('garageCar.goals.schedule.estimatedFinish')}
                amount={formatDate(estimatedFinish)}
                strong
              />
              {daysLate !== null && goal.deadline && (
                <NoteRow span={fullSpan} color="error">
                  {t('garageCar.goals.schedule.late', {
                    count: daysLate,
                    deadline: formatDate(parseISO(goal.deadline)),
                  })}
                </NoteRow>
              )}
              {unestimatedJobs > 0 && (
                <NoteRow span={fullSpan}>
                  {t('garageCar.goals.schedule.unestimated', {
                    count: unestimatedJobs,
                  })}
                </NoteRow>
              )}
            </>
          )}
        </TableFooter>
      )}
    </Table>
  );
}

/** A right-aligned remark under the totals ("not included", "after the deadline"). */
function NoteRow({
  span,
  color = 'text.secondary',
  children,
}: {
  span: number;
  color?: 'text.secondary' | 'error';
  children: string;
}) {
  return (
    <TableRow>
      <TableCell colSpan={span} align="right" sx={{ borderBottom: 0 }}>
        <Typography variant="caption" color={color}>
          {children}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

interface TotalsRowProps {
  span: number;
  /** Whether the table has an actions column to leave empty on the right. */
  trailing: boolean;
  label: string;
  amount: string | null;
  strong?: boolean;
}

function TotalsRow({ span, trailing, label, amount, strong = false }: TotalsRowProps) {
  const variant = strong ? 'subtitle2' : 'body2';
  const color = strong ? 'text.primary' : 'text.secondary';

  return (
    <TableRow>
      <TableCell colSpan={span} align="right" sx={{ borderBottom: 0 }}>
        <Typography variant={variant} color={color}>
          {label}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ borderBottom: 0, whiteSpace: 'nowrap' }}>
        <Typography variant={variant} color={color}>
          {amount}
        </Typography>
      </TableCell>
      {trailing && <TableCell sx={{ borderBottom: 0 }} />}
    </TableRow>
  );
}
